import React, { useEffect, useMemo, useState } from "react";
import {
    Activity, Users, FileBarChart, Download, RefreshCw, Search,
    Heart, Receipt, ClipboardList, Ticket, Package, HandHeart, Hammer,
    Vote, ShieldCheck, User as UserIcon, Edit3, Calendar, FileText, Mail,
} from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

/**
 * RecordsPanel — Automated record-keeping hub for admins.
 *
 * Three sub-views (no schema changes, all derived from existing data):
 *   • Audit Log — unified chronological event feed across donations,
 *     payouts, spend requests, raffles, orders, volunteers, contractors,
 *     polls, legal records, content edits, user logins.  CSV export.
 *   • Donors — every donor email auto-aggregated with lifetime totals,
 *     last gift date, classroom tags.  Click row → full giving history.
 *   • Monthly Reports — board-ready PDF auto-generated on the 1st of each
 *     month; admins can also trigger any prior month on demand.
 */
const ICON_MAP = {
    heart: Heart, receipt: Receipt, clipboard: ClipboardList, ticket: Ticket,
    package: Package, hand: HandHeart, hammer: Hammer, vote: Vote, shield: ShieldCheck,
    user: UserIcon, edit: Edit3,
};

const RecordsPanel = () => {
    const [sub, setSub] = useState("stewardship");
    return (
        <div data-testid="records-panel">
            <div className="flex items-start gap-3 mb-6">
                <Activity className="w-5 h-5 text-[#E53935] mt-1" strokeWidth={1.5} />
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Records & Automation</h2>
                    <p className="text-sm text-[#4A5568] mt-1.5 max-w-3xl">
                        Automated record-keeping. Every donation, payout, raffle ticket, vote, and admin
                        edit lands in the audit log — no manual entry. Donor profiles roll up automatically.
                        A monthly compliance report PDF is built on the 1st of each month and emailed to the board.
                    </p>
                </div>
            </div>

            <div className="flex gap-2 mb-6 border-b border-black/10 overflow-x-auto">
                {[
                    { id: "stewardship", label: "Stewardship", icon: Heart },
                    { id: "audit-log", label: "Audit Log", icon: Activity },
                    { id: "donors", label: "Donor profiles", icon: Users },
                    { id: "monthly", label: "Monthly reports", icon: FileBarChart },
                ].map((t) => {
                    const Icon = t.icon;
                    const active = sub === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setSub(t.id)}
                            data-testid={`records-tab-${t.id}-btn`}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                                active
                                    ? "border-[#E53935] text-[#E53935]"
                                    : "border-transparent text-[#4A5568] hover:text-[#1C252E]"
                            }`}
                        >
                            <Icon className="w-4 h-4" strokeWidth={1.5} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {sub === "stewardship" && <StewardshipView />}
            {sub === "audit-log" && <AuditLogView />}
            {sub === "donors" && <DonorsView />}
            {sub === "monthly" && <MonthlyReportsView />}
        </div>
    );
};

// =========================================================
// Stewardship dashboard — who needs a thank-you next
// =========================================================
const StewardshipView = () => {
    const [data, setData] = useState(null);
    const [composerEmail, setComposerEmail] = useState(null);
    const [digestBusy, setDigestBusy] = useState(false);
    const [digests, setDigests] = useState([]);
    const fetchDash = async () => {
        try {
            const r = await api.get(`/admin/records/stewardship`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load stewardship dashboard");
        }
    };
    const fetchDigests = async () => {
        try {
            const r = await api.get(`/admin/records/stewardship/digests`);
            setDigests(r.data.items || []);
        } catch {/* non-fatal */}
    };
    useEffect(() => { fetchDash(); fetchDigests(); }, []);

    const sendDigestToMe = async () => {
        setDigestBusy(true);
        try {
            const r = await api.post(`/admin/records/stewardship/digest/send`);
            toast.success(`Digest sent to ${r.data.sent_to}`);
            fetchDigests();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setDigestBusy(false);
        }
    };
    const sendDigestToBoard = async () => {
        const boardAddress = window.prompt("Send digest to which address?", "board@reddogfoundation.org");
        if (!boardAddress) return;
        setDigestBusy(true);
        try {
            const r = await api.post(`/admin/records/stewardship/digest/send?to=${encodeURIComponent(boardAddress)}`);
            toast.success(`Digest sent to ${r.data.sent_to}`);
            fetchDigests();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setDigestBusy(false);
        }
    };

    if (!data) return <p className="text-sm text-[#4A5568]">Loading…</p>;

    const totalActions = data.summary.needs_thanks + data.summary.milestones + data.summary.top_untouched;
    const lastDigest = digests[0];

    return (
        <div data-testid="records-stewardship">
            {/* Ambient digest strip */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#1C252E] text-white rounded-2xl p-5 mb-5" data-testid="stew-digest-strip">
                <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#F2D8D5]">Weekly digest</p>
                    <p className="text-sm mt-1">
                        An automatic summary of this dashboard ships to <strong>BOARD_EMAIL</strong> every Monday at ~08:00 UTC.
                        {lastDigest && (
                            <span className="text-xs text-white/70 ml-1">
                                Last sent {(lastDigest.sent_at || "").slice(0, 10)} → {lastDigest.sent_to}
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={sendDigestToMe}
                        disabled={digestBusy}
                        data-testid="stew-digest-send-me-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-white text-[#1C252E] hover:bg-[#F4F1EA] disabled:opacity-60"
                    >
                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                        Email me a copy
                    </button>
                    <button
                        type="button"
                        onClick={sendDigestToBoard}
                        disabled={digestBusy}
                        data-testid="stew-digest-send-board-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-white/30 text-white hover:bg-white/10 disabled:opacity-60"
                    >
                        Send to address…
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <SummaryCard label="Needs thanks" count={data.summary.needs_thanks}
                             sub={`Last ${data.summary.window_days} days`} tone="red" testid="stew-summary-needs" />
                <SummaryCard label="Milestone crossings" count={data.summary.milestones}
                             sub="$500 · $1k · $5k · $10k" tone="cream" testid="stew-summary-milestones" />
                <SummaryCard label="Top untouched" count={data.summary.top_untouched}
                             sub="Never personally thanked" tone="cream" testid="stew-summary-untouched" />
            </div>

            {totalActions === 0 && (
                <div className="bg-[#F4F1EA] rounded-2xl p-8 text-center" data-testid="stew-empty">
                    <Heart className="w-10 h-10 mx-auto text-[#E53935] mb-3" strokeWidth={1.5} />
                    <h3 className="font-heading text-xl text-[#1C252E] font-medium">You&apos;re all caught up</h3>
                    <p className="text-sm text-[#4A5568] mt-1.5">No outstanding stewardship actions right now. Check back next week — milestones and new gifts will surface here automatically.</p>
                </div>
            )}

            {data.needs_thanks.length > 0 && (
                <Section title="Recent givers needing a thank-you" subtitle={`Donors who gave in the last ${data.summary.window_days} days without a personal acknowledgment`}>
                    <ul className="divide-y divide-black/5" data-testid="stew-needs-list">
                        {data.needs_thanks.map((r) => (
                            <li key={r.email} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3" data-testid={`stew-needs-row-${r.email}`}>
                                <div className="min-w-0">
                                    <p className="font-medium text-[#1C252E]">{r.donor_name || r.email}</p>
                                    <p className="text-xs text-[#4A5568]">
                                        ${r.last_amount.toFixed(2)} on {(r.last_gift_at || "").slice(0, 10)} · lifetime ${r.lifetime.toFixed(2)} · {r.reason}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setComposerEmail(r.email)}
                                    data-testid={`stew-thank-${r.email}-btn`}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F] flex-shrink-0"
                                >
                                    <Heart className="w-4 h-4" strokeWidth={1.5} />
                                    Thank now
                                </button>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {data.milestones.length > 0 && (
                <Section title="Milestone crossings" subtitle="Acknowledge donors who just hit a new lifetime tier">
                    <ul className="divide-y divide-black/5" data-testid="stew-milestones-list">
                        {data.milestones.map((r) => (
                            <li key={r.email} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3" data-testid={`stew-milestone-row-${r.email}`}>
                                <div className="min-w-0">
                                    <p className="font-medium text-[#1C252E]">
                                        {r.donor_name || r.email}{" "}
                                        <span className="text-xs ml-1 px-2 py-0.5 rounded-full bg-[#F2D8D5] text-[#E53935]">
                                            ${Math.round(r.tier).toLocaleString()}
                                        </span>
                                    </p>
                                    <p className="text-xs text-[#4A5568]">Lifetime ${r.lifetime.toFixed(2)} · {r.gift_count} gift(s) · {r.reason}</p>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setComposerEmail(r.email)}
                                        data-testid={`stew-milestone-thank-${r.email}-btn`}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F]"
                                    >
                                        <Heart className="w-4 h-4" strokeWidth={1.5} />
                                        Thank
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`stew-milestone-ack-${r.email}-btn`}
                                        onClick={async () => {
                                            try {
                                                await api.post(`/admin/records/donors/${encodeURIComponent(r.email)}/milestone-ack`, { tier: r.tier });
                                                toast.success(`Marked $${Math.round(r.tier).toLocaleString()} milestone as acknowledged`);
                                                fetchDash();
                                            } catch (e) {
                                                toast.error(e?.response?.data?.detail || "Could not mark acknowledged");
                                            }
                                        }}
                                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium border border-black/10 text-[#4A5568] hover:border-[#1C252E] hover:text-[#1C252E]"
                                    >
                                        Mark acknowledged
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {data.top_untouched.length > 0 && (
                <Section
                    title="Top untouched donors"
                    subtitle="Top 10 lifetime givers who have never received a personal thank-you — perfect for a quarterly sweep"
                    headerRight={
                        <button
                            type="button"
                            data-testid="stew-quarterly-sweep-btn"
                            onClick={() => setComposerEmail(data.top_untouched[0].email)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#1C252E] text-white hover:bg-black"
                        >
                            <Heart className="w-4 h-4" strokeWidth={1.5} />
                            Start quarterly sweep
                        </button>
                    }
                >
                    <ul className="divide-y divide-black/5" data-testid="stew-untouched-list">
                        {data.top_untouched.map((r, idx) => (
                            <li key={r.email} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3" data-testid={`stew-untouched-row-${r.email}`}>
                                <div className="min-w-0 flex items-center gap-3">
                                    <span className="text-xs font-bold text-[#4A5568] w-5 text-right">#{idx + 1}</span>
                                    <div>
                                        <p className="font-medium text-[#1C252E]">{r.donor_name || r.email}</p>
                                        <p className="text-xs text-[#4A5568]">
                                            Lifetime ${r.lifetime.toFixed(2)} · {r.gift_count} gift(s) · last {(r.last_gift_at || "").slice(0, 10) || "—"}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setComposerEmail(r.email)}
                                    data-testid={`stew-untouched-thank-${r.email}-btn`}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-[#E53935] text-[#E53935] hover:bg-[#E53935] hover:text-white flex-shrink-0"
                                >
                                    <Heart className="w-4 h-4" strokeWidth={1.5} />
                                    Thank
                                </button>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {composerEmail && (
                <ThankYouComposer
                    email={composerEmail}
                    onClose={() => setComposerEmail(null)}
                    onSent={() => { setComposerEmail(null); fetchDash(); }}
                />
            )}
        </div>
    );
};

const SummaryCard = ({ label, count, sub, tone, testid }) => {
    const bg = tone === "red" ? "bg-[#F2D8D5]" : "bg-[#F4F1EA]";
    const accent = tone === "red" ? "text-[#E53935]" : "text-[#1C252E]";
    return (
        <div className={`${bg} rounded-2xl p-5`} data-testid={testid}>
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5568] mb-1.5">{label}</p>
            <p className={`font-heading text-4xl ${accent} font-medium`}>{count}</p>
            <p className="text-xs text-[#4A5568] mt-1.5">{sub}</p>
        </div>
    );
};

const Section = ({ title, subtitle, children, headerRight }) => (
    <div className="bg-white border border-black/5 rounded-2xl p-5 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-3">
            <div>
                <h3 className="font-heading text-lg text-[#1C252E] font-medium">{title}</h3>
                <p className="text-xs text-[#4A5568] mt-0.5">{subtitle}</p>
            </div>
            {headerRight}
        </div>
        {children}
    </div>
);

// =========================================================
// Audit Log
// =========================================================
const AuditLogView = () => {
    const [data, setData] = useState({ events: [], kinds_available: [], count: 0 });
    const [kinds, setKinds] = useState(new Set());
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    const fetchLog = async () => {
        setLoading(true);
        try {
            const k = Array.from(kinds).join(",");
            const r = await api.get(`/admin/records/audit-log`, { params: { kinds: k, search, limit: 500 } });
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load audit log");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLog(); }, []);

    const toggleKind = (id) => {
        setKinds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const downloadCsv = async () => {
        const k = Array.from(kinds).join(",");
        const url = new URL(`${API}/admin/records/audit-log.csv`);
        if (k) url.searchParams.set("kinds", k);
        if (search) url.searchParams.set("search", search);
        const token = localStorage.getItem("rdf_token");
        const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
        if (!resp.ok) { toast.error("CSV download failed"); return; }
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `reddog-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
        toast.success("CSV downloaded");
    };

    return (
        <div data-testid="records-audit-log">
            <div className="bg-white border border-black/5 rounded-2xl p-5 mb-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" strokeWidth={1.5} />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchLog()}
                            placeholder="Search actor or summary..."
                            data-testid="records-audit-search"
                            className="w-full rounded-xl border border-black/10 pl-10 pr-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={fetchLog}
                        disabled={loading}
                        data-testid="records-audit-refresh-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-[#1C252E] text-white hover:bg-black disabled:opacity-60"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={downloadCsv}
                        data-testid="records-audit-csv-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border border-black/10 text-[#1C252E] hover:border-[#E53935] hover:text-[#E53935]"
                    >
                        <Download className="w-4 h-4" strokeWidth={1.5} />
                        CSV export
                    </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {data.kinds_available.map((k) => {
                        const active = kinds.has(k.id);
                        return (
                            <button
                                key={k.id}
                                type="button"
                                onClick={() => toggleKind(k.id)}
                                data-testid={`records-audit-filter-${k.id}-btn`}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    active
                                        ? "bg-[#E53935] text-white"
                                        : "bg-[#F4F1EA] text-[#4A5568] hover:bg-[#F2D8D5]"
                                }`}
                            >
                                {k.label}
                            </button>
                        );
                    })}
                    {kinds.size > 0 && (
                        <button
                            type="button"
                            onClick={() => setKinds(new Set())}
                            data-testid="records-audit-clear-filters-btn"
                            className="px-3 py-1 rounded-full text-xs font-medium text-[#E53935] hover:underline"
                        >
                            Clear
                        </button>
                    )}
                </div>
                <p className="text-xs text-[#4A5568] mt-3" data-testid="records-audit-count">
                    Showing {data.count} of last {data.events.length === 500 ? "500 (limit)" : data.count} events
                </p>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl divide-y divide-black/5" data-testid="records-audit-list">
                {data.events.length === 0 && !loading && (
                    <p className="p-8 text-center text-sm text-[#4A5568]">No events found.</p>
                )}
                {data.events.map((ev) => {
                    const Icon = ICON_MAP[ev.icon] || FileText;
                    const date = ev.ts ? new Date(ev.ts).toLocaleString() : "";
                    return (
                        <div
                            key={`${ev.kind}-${ev.ref_id}-${ev.ts}`}
                            data-testid={`records-audit-row-${ev.kind}`}
                            className="flex items-start gap-3 p-4 hover:bg-[#FDFBF7] transition-colors"
                        >
                            <div className="w-8 h-8 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                                <Icon className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs uppercase tracking-wider font-bold text-[#E53935]">{ev.label}</span>
                                    {ev.amount != null && (
                                        <span className="text-xs font-bold text-[#1C252E]">${Number(ev.amount).toFixed(2)}</span>
                                    )}
                                </div>
                                <p className="text-sm text-[#1C252E] mt-0.5 truncate">{ev.summary}</p>
                                <p className="text-xs text-[#4A5568] mt-0.5">{date} · {ev.actor || "—"}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// =========================================================
// Donor profiles
// =========================================================
const DonorsView = () => {
    const [data, setData] = useState({ donors: [], count: 0 });
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("lifetime");
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);

    const fetchDonors = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/records/donors`, { params: { search, sort, limit: 500 } });
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load donors");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { fetchDonors(); }, [sort]);

    return (
        <div data-testid="records-donors">
            <div className="bg-white border border-black/5 rounded-2xl p-5 mb-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" strokeWidth={1.5} />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchDonors()}
                            placeholder="Search by name or email..."
                            data-testid="records-donors-search"
                            className="w-full rounded-xl border border-black/10 pl-10 pr-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        data-testid="records-donors-sort"
                        className="rounded-xl border border-black/10 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                    >
                        <option value="lifetime">Sort: Lifetime giving</option>
                        <option value="last_gift">Sort: Last gift</option>
                        <option value="count">Sort: Gift count</option>
                        <option value="name">Sort: Name (A–Z)</option>
                    </select>
                    <button
                        type="button"
                        onClick={fetchDonors}
                        disabled={loading}
                        data-testid="records-donors-refresh-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium bg-[#1C252E] text-white hover:bg-black disabled:opacity-60"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        Refresh
                    </button>
                </div>
                <p className="text-xs text-[#4A5568] mt-3">{data.count} unique donors</p>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="records-donors-table">
                        <thead className="bg-[#F4F1EA]">
                            <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                                <th className="px-4 py-3">Donor</th>
                                <th className="px-4 py-3 text-right">Lifetime</th>
                                <th className="px-4 py-3 text-right">Gifts</th>
                                <th className="px-4 py-3">Last gift</th>
                                <th className="px-4 py-3">Class tags</th>
                                <th className="px-4 py-3 text-right">View</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {data.donors.map((d) => (
                                <tr key={d.email} data-testid={`records-donor-row-${d.email}`} className="hover:bg-[#FDFBF7]">
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-[#1C252E]">{d.donor_name || "—"}</p>
                                        <p className="text-xs text-[#4A5568]">{d.email}</p>
                                    </td>
                                    <td className="px-4 py-3 text-right font-bold text-[#E53935]">${d.lifetime.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right">{d.gift_count}</td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">
                                        {d.last_gift_at ? new Date(d.last_gift_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {(d.class_tags || []).slice(0, 2).map((t, i) => (
                                                <span key={i} className="bg-[#F2D8D5] text-[#1C252E] text-[10px] px-2 py-0.5 rounded-full">{t}</span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            type="button"
                                            onClick={() => setSelected(d.email)}
                                            data-testid={`records-donor-view-${d.email}-btn`}
                                            className="text-xs font-medium text-[#E53935] hover:underline"
                                        >
                                            Details →
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {data.donors.length === 0 && !loading && (
                                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-[#4A5568]">No donors yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && <DonorDetailModal email={selected} onClose={() => setSelected(null)} />}
        </div>
    );
};

const DonorDetailModal = ({ email, onClose }) => {
    const [data, setData] = useState(null);
    const [outreach, setOutreach] = useState(null); // null | "thank-you" | "tax-letter"
    const [historyOpen, setHistoryOpen] = useState(false);

    useEffect(() => {
        api.get(`/admin/records/donors/${encodeURIComponent(email)}`)
            .then((r) => setData(r.data))
            .catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load donor"); onClose(); });
    }, [email, onClose]);

    const giftYears = useMemo(() => {
        if (!data) return [];
        const years = new Set();
        data.donations.forEach((d) => {
            const y = (d.created_at || "").slice(0, 4);
            if (y) years.add(parseInt(y, 10));
        });
        return Array.from(years).sort((a, b) => b - a);
    }, [data]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="records-donor-detail-modal"
            >
                {!data ? (
                    <p className="text-sm text-[#4A5568]">Loading…</p>
                ) : (
                    <>
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h3 className="font-heading text-2xl text-[#1C252E] font-medium">{data.donor_name || data.email}</h3>
                                <p className="text-sm text-[#4A5568]">{data.email}</p>
                            </div>
                            <button onClick={onClose} className="text-[#4A5568] hover:text-[#1C252E]" data-testid="records-donor-detail-close-btn">✕</button>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mb-6">
                            <div className="bg-[#F2D8D5] rounded-xl p-4 text-center">
                                <p className="text-xs uppercase tracking-wider text-[#4A5568]">Donations</p>
                                <p className="font-heading text-2xl text-[#E53935] font-medium">${data.lifetime_donations.toFixed(2)}</p>
                            </div>
                            <div className="bg-[#F4F1EA] rounded-xl p-4 text-center">
                                <p className="text-xs uppercase tracking-wider text-[#4A5568]">Raffle spend</p>
                                <p className="font-heading text-2xl text-[#1C252E] font-medium">${data.raffle_spend.toFixed(2)}</p>
                            </div>
                            <div className="bg-[#F4F1EA] rounded-xl p-4 text-center">
                                <p className="text-xs uppercase tracking-wider text-[#4A5568]">Shop spend</p>
                                <p className="font-heading text-2xl text-[#1C252E] font-medium">${data.shop_spend.toFixed(2)}</p>
                            </div>
                        </div>

                        {/* Stewardship outreach actions */}
                        <div className="bg-[#FDFBF7] border border-[#E53935]/15 rounded-2xl p-4 mb-6">
                            <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#E53935] mb-2">Stewardship actions</p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => setOutreach("thank-you")}
                                    data-testid="records-donor-thankyou-btn"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F]"
                                >
                                    <Heart className="w-4 h-4" strokeWidth={1.5} />
                                    Send personal thank-you
                                </button>
                                {giftYears.map((y) => (
                                    <button
                                        key={y}
                                        type="button"
                                        onClick={() => setOutreach(`tax-letter:${y}`)}
                                        data-testid={`records-donor-tax-letter-${y}-btn`}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-black/15 text-[#1C252E] hover:border-[#E53935] hover:text-[#E53935]"
                                    >
                                        <FileText className="w-4 h-4" strokeWidth={1.5} />
                                        Send {y} tax letter
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setHistoryOpen((v) => !v)}
                                    data-testid="records-donor-outreach-history-btn"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-[#4A5568] hover:text-[#1C252E]"
                                >
                                    <Activity className="w-4 h-4" strokeWidth={1.5} />
                                    {historyOpen ? "Hide" : "Show"} outreach history
                                </button>
                            </div>
                            <p className="text-xs text-[#4A5568] mt-2">
                                Tax letters include the donor&apos;s annual giving PDF plus the Foundation EIN letter and Articles of Incorporation for their CPA.
                            </p>
                            {historyOpen && <OutreachHistory email={email} />}
                        </div>

                        <h4 className="font-heading text-lg text-[#1C252E] font-medium mb-2">Donations ({data.donations.length})</h4>
                        <ul className="text-sm divide-y divide-black/5 mb-4">
                            {data.donations.map((d) => (
                                <li key={d.id} className="py-2 flex justify-between">
                                    <span className="text-[#4A5568]">{(d.created_at || "").slice(0, 10)} · {d.campaign_id ? "Campaign" : "General"}</span>
                                    <span className="font-bold text-[#1C252E]">${Number(d.amount || 0).toFixed(2)}</span>
                                </li>
                            ))}
                            {data.donations.length === 0 && <li className="py-2 text-[#4A5568]">No donations.</li>}
                        </ul>
                        {data.raffle_entries.length > 0 && (
                            <>
                                <h4 className="font-heading text-lg text-[#1C252E] font-medium mb-2">Raffle entries ({data.raffle_entries.length})</h4>
                                <ul className="text-sm divide-y divide-black/5 mb-4">
                                    {data.raffle_entries.map((r) => (
                                        <li key={r.id} className="py-2 flex justify-between">
                                            <span className="text-[#4A5568]">{(r.paid_at || "").slice(0, 10)} · {r.ticket_count} ticket(s)</span>
                                            <span className="font-bold text-[#1C252E]">${Number(r.amount_paid || r.ticket_price || 0).toFixed(2)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        {data.orders.length > 0 && (
                            <>
                                <h4 className="font-heading text-lg text-[#1C252E] font-medium mb-2">Shop orders ({data.orders.length})</h4>
                                <ul className="text-sm divide-y divide-black/5">
                                    {data.orders.map((o) => (
                                        <li key={o.id} className="py-2 flex justify-between">
                                            <span className="text-[#4A5568]">{(o.paid_at || "").slice(0, 10)}</span>
                                            <span className="font-bold text-[#1C252E]">${Number(o.total || 0).toFixed(2)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </>
                )}
            </div>

            {outreach === "thank-you" && (
                <ThankYouComposer
                    email={email}
                    onClose={() => setOutreach(null)}
                    onSent={() => { setOutreach(null); setHistoryOpen(true); }}
                />
            )}
            {typeof outreach === "string" && outreach.startsWith("tax-letter:") && (
                <TaxLetterConfirm
                    email={email}
                    year={parseInt(outreach.split(":")[1], 10)}
                    onClose={() => setOutreach(null)}
                    onSent={() => { setOutreach(null); setHistoryOpen(true); }}
                />
            )}
        </div>
    );
};

// Composer modal for the personal thank-you (prefilled + editable).
const ThankYouComposer = ({ email, onClose, onSent }) => {
    const [preview, setPreview] = useState(null);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/admin/records/donors/${encodeURIComponent(email)}/thank-you/preview`)
            .then((r) => {
                setPreview(r.data);
                setSubject(r.data.subject);
                setBody(r.data.body);
            })
            .catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load preview"); onClose(); });
    }, [email, onClose]);

    const send = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/records/donors/${encodeURIComponent(email)}/thank-you`, { subject, body });
            toast.success(`Thank-you sent to ${preview?.donor_name || email}`);
            onSent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="records-donor-thankyou-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-heading text-2xl text-[#1C252E] font-medium">Personal thank-you</h3>
                        <p className="text-sm text-[#4A5568]">Sending to <strong>{email}</strong></p>
                    </div>
                    <button onClick={onClose} className="text-[#4A5568] hover:text-[#1C252E]" data-testid="records-donor-thankyou-close-btn">✕</button>
                </div>
                {!preview ? (
                    <p className="text-sm text-[#4A5568]">Loading template…</p>
                ) : (
                    <>
                        <label className="block text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Subject</label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            data-testid="records-donor-thankyou-subject-input"
                            className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm bg-white mb-4 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                        <label className="block text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Body</label>
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={14}
                            data-testid="records-donor-thankyou-body-input"
                            className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm bg-white mb-4 focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] font-mono"
                        />
                        <p className="text-xs text-[#4A5568] mb-4">
                            Prefilled with the donor&apos;s name, last gift amount, last campaign{preview?.last_campaign_name ? ` (${preview.last_campaign_name})` : ""}, and lifetime total — edit freely before sending.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="records-donor-thankyou-cancel-btn"
                                className="px-5 py-2.5 rounded-full text-sm font-medium border border-black/10 text-[#1C252E] hover:bg-[#F4F1EA]"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={send}
                                disabled={busy || !subject.trim() || !body.trim()}
                                data-testid="records-donor-thankyou-send-btn"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-60"
                            >
                                <Heart className="w-4 h-4" strokeWidth={1.5} />
                                {busy ? "Sending…" : "Send"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// Confirm modal for sending a tax letter (which has a fixed body — the editing
// surface is on the auto-generated PDF, not the email body).
const TaxLetterConfirm = ({ email, year, onClose, onSent }) => {
    const [busy, setBusy] = useState(false);
    const send = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/admin/records/donors/${encodeURIComponent(email)}/tax-letter/${year}`);
            toast.success(`${year} tax letter sent — ${r.data.donation_count} gift(s) · $${r.data.total_amount.toFixed(2)} · ${r.data.attachment_count} attachment(s)`);
            onSent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8"
                onClick={(e) => e.stopPropagation()}
                data-testid="records-donor-taxletter-modal"
            >
                <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-2">Send {year} tax letter?</h3>
                <p className="text-sm text-[#4A5568] mb-4">
                    A PDF showing every {year} gift will be generated and emailed to <strong>{email}</strong>, with the Foundation&apos;s
                    EIN confirmation letter and Articles of Incorporation attached for their CPA. Logged in the donor&apos;s outreach history.
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="records-donor-taxletter-cancel-btn"
                        className="px-5 py-2.5 rounded-full text-sm font-medium border border-black/10 text-[#1C252E] hover:bg-[#F4F1EA]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={send}
                        disabled={busy}
                        data-testid="records-donor-taxletter-send-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-60"
                    >
                        <FileText className="w-4 h-4" strokeWidth={1.5} />
                        {busy ? "Sending…" : "Send"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Outreach history strip — shows every personal/tax email sent to this donor.
const OutreachHistory = ({ email }) => {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get(`/admin/records/donors/${encodeURIComponent(email)}/thank-yous`)
            .then((r) => setItems(r.data.items))
            .catch(() => setItems([]));
    }, [email]);
    if (items === null) return <p className="text-xs text-[#4A5568] mt-3">Loading…</p>;
    if (items.length === 0) return <p className="text-xs text-[#4A5568] mt-3">No outreach yet for this donor.</p>;
    return (
        <ul className="mt-3 divide-y divide-black/5 border-t border-black/5" data-testid="records-donor-outreach-history-list">
            {items.map((i, idx) => (
                <li key={idx} className="py-2 text-xs flex flex-wrap justify-between gap-2">
                    <span className="text-[#4A5568]">
                        <strong className="text-[#1C252E]">{i.kind === "tax_letter" ? `Tax letter ${i.year}` : "Personal"}</strong> · {i.subject}
                    </span>
                    <span className="text-[#4A5568]">{(i.sent_at || "").slice(0, 16).replace("T", " ")} · {i.sent_by}</span>
                </li>
            ))}
        </ul>
    );
};

// =========================================================
// Monthly reports
// =========================================================
const MonthlyReportsView = () => {
    const [reports, setReports] = useState([]);
    const [busy, setBusy] = useState(false);
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth()); // default to prev month

    const fetchReports = async () => {
        try {
            const r = await api.get(`/admin/records/monthly-reports`);
            setReports(r.data.reports || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load reports");
        }
    };
    useEffect(() => { fetchReports(); }, []);

    const generate = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/records/monthly-report/${year}/${month}`);
            toast.success(`Monthly report ${year}-${String(month).padStart(2, "0")} ready`);
            fetchReports();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally {
            setBusy(false);
        }
    };

    const downloadPdf = async (y, m) => {
        const token = localStorage.getItem("rdf_token");
        const url = `${API}/admin/records/monthly-report/${y}/${m}.pdf`;
        const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
        if (!resp.ok) { toast.error("PDF download failed"); return; }
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `reddog-monthly-report-${y}-${String(m).padStart(2, "0")}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
    };

    const months = useMemo(() => [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
    ], []);

    return (
        <div data-testid="records-monthly">
            <div className="bg-white border border-black/5 rounded-2xl p-5 mb-4">
                <div className="flex items-start gap-3 mb-4">
                    <Calendar className="w-5 h-5 text-[#E53935] mt-0.5" strokeWidth={1.5} />
                    <div>
                        <h3 className="font-heading text-lg text-[#1C252E] font-medium">Auto-generated on the 1st of each month</h3>
                        <p className="text-sm text-[#4A5568]">The cron builds a PDF for the previous month, archives it here, and emails the board. You can also build any prior month on demand.</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="records-monthly-year-select" className="rounded-xl border border-black/10 px-3 py-2.5 text-sm bg-white">
                        {Array.from({ length: 5 }).map((_, i) => {
                            const y = now.getFullYear() - i;
                            return <option key={y} value={y}>{y}</option>;
                        })}
                    </select>
                    <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} data-testid="records-monthly-month-select" className="rounded-xl border border-black/10 px-3 py-2.5 text-sm bg-white">
                        {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    <button
                        type="button"
                        onClick={generate}
                        disabled={busy}
                        data-testid="records-monthly-generate-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-60"
                    >
                        <FileBarChart className="w-4 h-4" strokeWidth={1.5} />
                        {busy ? "Generating…" : "Build report"}
                    </button>
                </div>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="records-monthly-table">
                        <thead className="bg-[#F4F1EA]">
                            <tr className="text-left text-xs uppercase tracking-wider text-[#4A5568]">
                                <th className="px-4 py-3">Period</th>
                                <th className="px-4 py-3 text-right">Raised</th>
                                <th className="px-4 py-3 text-right">Donors</th>
                                <th className="px-4 py-3 text-right">Gifts</th>
                                <th className="px-4 py-3 text-right">Paid out</th>
                                <th className="px-4 py-3">Generated</th>
                                <th className="px-4 py-3 text-right">PDF</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {reports.map((r) => (
                                <tr key={`${r.year}-${r.month}`} data-testid={`records-monthly-row-${r.year}-${r.month}`} className="hover:bg-[#FDFBF7]">
                                    <td className="px-4 py-3 font-medium text-[#1C252E]">{months[r.month - 1]} {r.year}</td>
                                    <td className="px-4 py-3 text-right font-bold text-[#E53935]">${Number(r.total_raised).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-right">{r.unique_donor_count}</td>
                                    <td className="px-4 py-3 text-right">{r.gift_count}</td>
                                    <td className="px-4 py-3 text-right">${Number(r.total_paid_out).toFixed(2)}</td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">
                                        <span className="block">{(r.generated_at || "").slice(0, 10)}</span>
                                        <span className="text-[10px]">by {r.generated_by}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            type="button"
                                            onClick={() => downloadPdf(r.year, r.month)}
                                            data-testid={`records-monthly-download-${r.year}-${r.month}-btn`}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-[#E53935] hover:underline"
                                        >
                                            <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> PDF
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {reports.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-[#4A5568]">No monthly reports yet. Build one above, or wait for the 1st-of-month cron.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default RecordsPanel;
